import { Suspense, useState } from "react";
import { MapPin } from "lucide-react";
import { Outlet, Link, useNavigate } from "react-router";

import { PageLoader } from "@/components/PageLoader";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { GUEST_ONLY_ROUTES, PROTECTED_ROUTES, ROUTES } from "@/constants/routes";
import { NotificationBell } from "@/features/engagement/components/NotificationBell";
import { WishlistIcon } from "@/features/engagement/components/WishlistIcon";
import { ChatIcon } from "@/features/messaging/components/ChatIcon";
import { useAppSelector } from "@/store/hooks";

function initialsFromName(name: string): string {
  const [first, second] = name.trim().split(/\s+/);
  return `${first?.[0] ?? ""}${second?.[0] ?? ""}`.toUpperCase() || "?";
}

/**
 * docs/ui/layouts.md's `MainAppLayout` — used by every Public route (Home,
 * Search, Product Details, Help). Deliberately minimal: other nav links and
 * the footer need a canonical nav-link set and per-screen footer visibility
 * that `ui-architecture.md` §7/§9 flag as still unresolved — building that
 * content now would mean guessing at exactly what's blocked, not
 * implementing a decided spec.
 *
 * The search bar submits straight to `/search?q=...` (`useSearchFilters`
 * reads `q` from the URL, same contract `SearchResultsPage`'s own search box
 * uses) — hidden below `lg` since there's no room for it alongside the brand
 * and trailing actions on a narrow header; a mobile-specific search entry
 * point is a separate, not-yet-designed piece per `ui-architecture.md`'s
 * mobile home header row. The location indicator next to the brand is
 * chrome-only for now: `AuthUser` deliberately carries no location
 * (`authSlice.ts`'s own comment — the JWT-derived user is name/avatar only),
 * and wiring a real per-user location means fetching the full `OwnProfile`
 * in a layout that renders on every Public route, which is a separate
 * fetch/caching decision, not a header-markup one.
 *
 * The Sell CTA is the one exception, added once the Sell Product feature
 * existed: it links straight to `/sell/media` with no auth check here — a
 * guest clicking it is caught by `RequireAuth` (routes/guards/RequireAuth.tsx),
 * which bounces to `/login?redirect=/sell/media` and back on success, the
 * same redirect-then-return flow every other protected route already uses.
 * Without this link `/sell/media` was only reachable by typing the URL
 * directly — a real, now-fixed gap, not a stylistic choice.
 *
 * The wishlist, chat, and notification icons are the same kind of exception,
 * added once their features existed: docs/architecture/claude/ui-architecture.md's
 * own "Full app header" row lists Home/Search/Product Details alongside
 * every `AccountLayout` page as needing them — without them, an
 * authenticated user browsing these Public-layout pages had no way to reach
 * `/wishlist` at all (the page and `ProductCard`'s favorite-toggle heart
 * both worked, there was just no link to it anywhere) or see an unread
 * message/notification count until they happened to navigate to an
 * Account-layout page first. All three are guest-only, since a wishlist,
 * conversation, or notification is always the current user's own.
 *
 * The profile avatar/login link closes the same gap the bell did: without
 * it, there was no visual way on any Public-layout page to tell whether
 * you're logged in at all, let alone reach your own profile — a real,
 * user-reported gap, not a stylistic addition. Signed in shows the avatar
 * (linking to `/profile`, the same initials-fallback `AuthenticatedLayout`
 * already uses); signed out shows a plain "Log in" link instead of
 * guessing at a fuller guest nav `ui-architecture.md` hasn't decided yet.
 */
export function PublicLayout() {
  const isAuthenticated = useAppSelector((state) => state.auth.status === "authenticated");
  const user = useAppSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");

  function handleSearchSubmit(value: string) {
    const params = new URLSearchParams();
    if (value) params.set("q", value);
    navigate(`${ROUTES.search}?${params.toString()}`);
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="flex h-16 shrink-0 items-center gap-4 border-b border-border px-4">
        <div className="flex shrink-0 items-center gap-4">
          <Link to={ROUTES.home} className="text-h3 text-foreground">
            BazaarX
          </Link>
          <div className="hidden items-center gap-1.5 text-body-sm text-muted-foreground md:flex">
            <MapPin className="h-4 w-4" aria-hidden="true" />
            <span>All locations</span>
          </div>
        </div>
        <SearchInput
          value={searchValue}
          onChange={setSearchValue}
          onSubmit={handleSearchSubmit}
          placeholder="Search for products, brands, or services..."
          aria-label="Search BazaarX"
          className="hidden max-w-xl flex-1 rounded-md bg-muted lg:flex"
        />
        <div className="ml-auto flex shrink-0 items-center gap-3">
          <Button type="button" size="sm" asChild>
            <Link to={PROTECTED_ROUTES.sellMedia}>Sell</Link>
          </Button>
          {isAuthenticated && <WishlistIcon />}
          {isAuthenticated && <ChatIcon />}
          {isAuthenticated && <NotificationBell />}
          <ThemeToggle />
          {isAuthenticated ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to={PROTECTED_ROUTES.profile} aria-label="Your profile">
                  <Avatar size="md">
                    {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt="" />}
                    <AvatarFallback>{user ? initialsFromName(user.name) : null}</AvatarFallback>
                  </Avatar>
                </Link>
              </TooltipTrigger>
              <TooltipContent>Profile</TooltipContent>
            </Tooltip>
          ) : (
            <Button type="button" variant="outline" size="sm" asChild>
              <Link to={GUEST_ONLY_ROUTES.login}>Log in</Link>
            </Button>
          )}
        </div>
      </header>
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
